import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Select from "../../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../../Components/Select/ISelectProps";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Hooks";
import { ICenterPrinter, ICenterPrinterPayload } from "./ICenterPrinter";
import { centreMasterMappingCreate } from "../../../Redux/Slices/Booklet/Admin/CentreMasterMapping/CentreMasterMappingCreate";
import { printerMasterRead } from "../../../Redux/Slices/Booklet/Admin/PrinterMaster/PrinterMasterRead";
import { centreMasterRead } from "../../../Redux/Slices/Booklet/Admin/CentreMaster/CentreMatserRead";

interface CenterPrinterFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: Partial<ICenterPrinter>;
  setInitialValues: React.Dispatch<React.SetStateAction<Partial<ICenterPrinter>>>;
  response: any;
  centerprinterdata: any;
}

const CenterPrinterForm: FunctionComponent<CenterPrinterFormProps> = ({
  formRef,
  initialValues,
}) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(centreMasterRead());
    dispatch(printerMasterRead());
  }, [dispatch]);

  const { data: listCentersData } = useAppSelector(
    (state) => state.CentreMasterReadSlice
  );
  const { data: listPrintersData } = useAppSelector(
    (state) => state.PrinterMasterReadSlice
  );


  const schema = z.object({
    center_printer_id: z.string().default("0"),
    center_id: z.string().min(1, "Center is required"),
    printer_id: z.string().min(1, "Printer is required"),
    status: z.boolean(),
    is_delete: z.boolean().default(false),
  });

  type FormValues = z.infer<typeof schema>;

 
  const methods = useForm<FormValues>({
    defaultValues: {
      center_printer_id: initialValues.center_printer_id ?? "0",
      center_id: initialValues.center_id ?? "",
      printer_id: initialValues.printer_id ?? "",
      status: initialValues.status ?? true,
      is_delete: initialValues.is_delete ?? false,
    },
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const { handleSubmit, setValue, getValues, reset, watch } = methods;

  useEffect(() => {
    reset({
      center_printer_id: initialValues.center_printer_id ?? "0",
      center_id: initialValues.center_id ?? "",
      printer_id: initialValues.printer_id ?? "",
      status: initialValues.status ?? true,
      is_delete: initialValues.is_delete ?? false,
    });
  }, [initialValues, reset]);

  const centerOptions: AutocompleteOption[] = 
    listCentersData?.data && Array.isArray(listCentersData.data) 
      ? listCentersData.data.map((c: any) => ({
          label: c.center_name,
          value: c.center_id,
        }))
      : [];

  const printerOptions: AutocompleteOption[] = 
    listPrintersData?.data && Array.isArray(listPrintersData.data) 
      ? listPrintersData.data.map((p: any) => ({
          label: `${p.printer_name} (${p.model})`,
          value: p.printer_id,
        }))
      : [];

  const statusOptions: AutocompleteOption[] = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];


  const onSubmit: SubmitHandler<ICenterPrinterPayload> = (values) => {
    dispatch(
      centreMasterMappingCreate({
        center_printer_id: values.center_printer_id,
        center_id: values.center_id,
        printer_id: values.printer_id,
        status: values.status,
        is_delete: values.is_delete,
      })
    );
  };

  return (
    <FormProvider {...methods}>
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <div className="form_double_input">
          {/* Center */}
          <Select
            name="center_id"
            label="Center"
            options={centerOptions}
            value={
              centerOptions.find((opt) => opt.value === watch("center_id")) ?? null
            }
            onChange={(_, value) =>
              setValue("center_id", (value as AutocompleteOption)?.value, {
                shouldValidate: true,
              })
            }
          />

          {/* Printer */}
          <Select
            name="printer_id"
            label="Printer"
            options={printerOptions}
            value={
              printerOptions.find((opt) => opt.value === watch("printer_id")) ??
              null
            }
            onChange={(_, value) =>
              setValue("printer_id", (value as AutocompleteOption)?.value, {
                shouldValidate: true,
              })
            }
          />
        </div>

        <div className="form_double_input" style={{ marginTop: "20px" }}>
          {/* Status */}
          <Select
            name="status"
            label="Status"
            options={statusOptions}
            value={
              statusOptions.find((opt) => opt.value === getValues("status")) ??
              null
            }
            onChange={(_, value) =>
              setValue("status", (value as AutocompleteOption)?.value)
            }
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default memo(CenterPrinterForm);
